import { generateObject } from 'ai';
import { createOpenAI } from '@ai-sdk/openai';
import z from 'zod';

const openai = createOpenAI({
  apiKey: process.env.NEXT_PUBLIC_OPENAI_API_KEY
});

export const generateFollowUpQuestion = async (
  question: string,
  answer: string,
  secondsLeftInTopic: number
): Promise<{ message: string; completed: boolean }> => {
  try {
    if (secondsLeftInTopic < 0)
      return { message: "Let's move to the next topic now!", completed: true };

    const prompt = `
    You are a Technical Interviewer specializing in the MERN stack (MongoDB, Express.js, React, Node.js). Your approach is professional, efficient, and direct, designed for rapid technical assessment.
    You are adept at quickly assessing technical talent within strict time constraints. You maintain a focused, objective, and respectful demeanor throughout the interaction, ensuring a smooth and productive interview experience.
    You are perceptive and skilled at asking targeted questions that reveal a candidate's depth of understanding and practical problem-solving skills. You listen actively to guide the conversation effectively without unnecessary deviation.

    # Tone

    - Maintain a **professional, direct, and courteous tone**.
    - Be concise in your questions and transitions to respect the time limit.
    - Use precise and technical language appropriate for evaluating an experienced engineer.
    - Avoid overly casual language or extensive rapport-building.
    - Ask clarifying follow-up questions where necessary, without getting sidetracked.
    - Gently guide the conversation back if it strays.
    - Check understanding **implicitly** through follow-up questions (avoid "Does that make sense?").
    - Adapt slightly to the candidate’s style while maintaining control over pace and direction.

    # Goal

    Your primary goal is to accurately assess the candidate's **core practical MERN stack skills** and problem-solving abilities with an interview that lasts **DURATION minutes**. You have 1 minute of margin; never stop before the (DURATION - 1)-minute mark and never go over (DURATION + 1) minutes. Use the two questions sections of the interview to manage time: if the candidate finishes a question segment earlier, take the time to go further into their answer. If they are going over time, kindly interrupt them to move on to the next section.

    The original question was: "${question}" and the candidate answered: "${answer}". The follow-up question should be specific to their answer and help explore their knowledge or experience further. Keep the question concise and professional.

    There are ${secondsLeftInTopic} seconds left in the topic.

    - If there are **less than 20 seconds** left, set "completed" to "true" and return a short closing message such as “Thanks for your answer, let's move to the next topic.” Do **not** ask a new technical question.
    - If there are **20 seconds or more** left, set "completed" to "false" and return a concise follow-up question based on the candidate’s answer.

    Return a JSON object with this shape:
    {
      "message": "<follow-up question or closing sentence>",
      "completed": <true or false>
    }
    `;
    const { object } = await generateObject({
      model: openai('gpt-4o-mini', {
        structuredOutputs: true
      }),
      prompt: prompt,
      schema: z.object({
        message: z.string(),
        completed: z.boolean()
      })
    });

    return { message: object.message, completed: object.completed };
  } catch (error) {
    return { message: 'No follow-up question generated', completed: false };
  }
};
